import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import SocialIntegrationResource from 'flow/api/socialintegration';
import { parseUrlParameter } from 'flow/utils/browser-utils';
import { store, history } from 'flow/configure-store';
import Actions from 'flow/actions';
import { updateApplicationState } from 'flow/actions/app-actions';
import { setCTIApiHost } from 'flow/utils/is-integration';
import { MSGOO_APP_REG_DEFAULT_VALUE } from 'flow/utils/social-integration-utils';
import { SYNC_SOURCE } from 'flow/utils/constants/app-constants';

const CANCEL_SIGN_IN_MESSAGE = 'cancel-sign-in';

const CtiSSO = () => {
    const token = parseUrlParameter('token');
    const redirectURL = '/signin';
    const [render, setRender] = useState(false);
    setCTIApiHost();
    //the google and microsoft login page will redirect to this module
    if (token) {
        window.opener.postMessage({ token: token });
    } else {
        setRender(true);
    }
    return (
        <React.Fragment>
            {render && <Redirect to={redirectURL} />}
        </React.Fragment>
    );
};

export const startCtiExternalSignIn = (syncSource, appRegistrationId) => {
    const callbackUrl = `${window.location.origin}/auth/integration/sso`;

    //receiveExternalSigninData pull signin data
    store.dispatch(
        receiveExternalSigninData({
            waiting: true,
            syncSource: syncSource,
        }),
    );

    SocialIntegrationResource.startExternalSignIn(
        {
            onResult: (result) => {
                //open new tab with syncSource login page
                const windowRef = window.open(result);

                //listen to the message from google/microsoft login tab
                window.addEventListener('message', function login(event) {
                    if (event.origin !== window.location.origin) return;
                    window.removeEventListener('message', login); // remove listener

                    if (event.data === CANCEL_SIGN_IN_MESSAGE) {
                        dispatchError(syncSource, CANCEL_SIGN_IN_MESSAGE);
                        windowRef.close();
                        return;
                    }

                    const { token } = event.data;
                    SocialIntegrationResource.finishExternalSignIn(
                        {
                            onResult(result) {
                                if (result.signInStatus === 'ok') {
                                    // successful login, redirect to refresh the app
                                    store.dispatch(
                                        updateApplicationState('boot'),
                                    );
                                    history.push('/');
                                } else {
                                    dispatchError(
                                        syncSource,
                                        result.signInStatus,
                                    );
                                }
                            },
                            onError(error) {
                                dispatchError(
                                    syncSource,
                                    error.__state.statusCode,
                                );
                            },
                            onClientError(error) {
                                dispatchError(
                                    syncSource,
                                    error.__state.statusCode,
                                );
                            },
                        },
                        token,
                        syncSource,
                    );

                    windowRef.close();
                });
            },
            onError: (error) => {
                dispatchError(syncSource, error.__state.statusCode);
            },
            onClientError: (error) => {
                dispatchError(syncSource, error.__state.statusCode);
            },
        },
        syncSource,
        appRegistrationId,
        'branding-1',
        null,
        callbackUrl,
    );
};

const getExternalSignInErrorMessage = (syncSource, statusCode) => {
    const data = {
        error: true,
        syncSource: syncSource,
        waiting: false,
    };

    switch (statusCode) {
        case 400:
        case 401:
        case 404:
            data.loginFail = true;
            break;
        case 503:
            data.serverFail = true;
            break;
        default:
            data.unknownError = true;
    }
    return data;
};

const dispatchError = (syncSource, statusCode) => {
    const data = getExternalSignInErrorMessage(syncSource, statusCode);
    store.dispatch(receiveExternalSigninData(data));
};

const receiveExternalSigninData = (data) => ({
    type: Actions.APP_EXTERNAL_SIGNIN_DATA,
    data: data,
});

export const startCtiGoogleSignIn = () => {
    startCtiExternalSignIn(SYNC_SOURCE.google, MSGOO_APP_REG_DEFAULT_VALUE);
};

export const startCtiMicrosoftSignIn = () => {
    startCtiExternalSignIn(SYNC_SOURCE.microsoft, MSGOO_APP_REG_DEFAULT_VALUE);
};

export const startCtiOidcSignIn = (appRegistrationId) => {
    startCtiExternalSignIn(SYNC_SOURCE.oidc, appRegistrationId);
};

export const cancelCtiExternalSignIn = () => {
    window.postMessage(CANCEL_SIGN_IN_MESSAGE);

    store.dispatch(
        receiveExternalSigninData({
            waiting: false,
            error: false,
        }),
    );
};

export default React.memo(CtiSSO);
